import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CATEGORIES, CategoryDefinition, DEFAULT_CATEGORY } from '../config/categoryConfig';
import { Layout } from '../components/layout';
import StartCategoryCircle from '../components/start/StartCategoryCircle';
import StartCategoryGrid from '../components/start/StartCategoryGrid';
import StartBackgroundTicker from '../components/start/StartBackgroundTicker';
import { useCategoryLocking } from '../hooks/useCategoryLocking/index';
import { StockCategoryFilter } from '../types/stockCategories';

export default function StartPage() {
  const navigate = useNavigate();
  const { isCategoryLocked, CategoryLockModal } = useCategoryLocking();

  // Desktop state: hover
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  // Mobile state: selection (first tap shows description)
  const [selectedCategoryMobile, setSelectedCategoryMobile] = useState<string | null>(null);

  /**
   * Check if a category is locked based on premium status (memoized)
   */
  const isCategoryLockedMemo = useCallback((categorySlug: string): boolean => {
    return isCategoryLocked(categorySlug as StockCategoryFilter);
  }, [isCategoryLocked]);

  /**
   * Handle category click (desktop) or tap (mobile) - memoized
   * Desktop: Click immediately navigates or shows modal
   * Mobile: First tap selects, second tap or Continue navigates
   */
  const handleCategoryClick = useCallback((category: CategoryDefinition) => {
    // Navigate to root if this is the default category, otherwise use slug
    // Premium categories show the GameStartOverlay with an upgrade prompt on Play
    const path = category.slug === DEFAULT_CATEGORY ? '/' : `/${category.slug}`;
    navigate(path);
  }, [navigate]);

  /**
   * Handle mobile tap: first tap selects, second tap navigates - memoized
   */
  const handleMobileTap = useCallback((category: CategoryDefinition) => {
    if (selectedCategoryMobile === category.slug) {
      // Second tap: navigate or show modal
      handleCategoryClick(category);
    } else {
      // First tap: select category to show description
      setSelectedCategoryMobile(category.slug);
    }
  }, [selectedCategoryMobile, handleCategoryClick]);

  /**
   * Handle mobile Continue button - memoized
   */
  const handleMobileContinue = useCallback((category: CategoryDefinition) => {
    handleCategoryClick(category);
  }, [handleCategoryClick]);

  return (
    <Layout hideHeader={true}>
      <Helmet>
        <title>Wallstreetle | Play the Best Stock Guessing Game</title>
        <meta name="description" content="Play Wallstreetle - the ultimate stock wordle, finance wordle, and stock guessing game! Choose from multiple categories for ticker guessing including SPY, Tech, NASDAQ, and WallStreetBets. Free daily stock challenge with charts, hints, and metrics. The best guess the stock game online." />
        <meta name="keywords" content="stock wordle, finance wordle, stock guessing game, guess the stock game, ticker guessing, equity game, stock trivia, investing puzzle, daily stock challenge, daily stock puzzle, stock market game, play stock game, free stock game" />
        <link rel="canonical" href="https://wallstreetle.com/start" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | Play the Best Stock Guessing Game" />
        <meta property="og:description" content="The ultimate stock wordle and finance wordle! Choose from multiple categories for this guess the stock game. Daily stock challenge with ticker guessing, charts, and hints. Free equity game for investors and traders." />
        <meta property="og:url" content="https://wallstreetle.com/start" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Wallstreetle - Stock Guessing Game Categories",
            "description": "Play the ultimate stock wordle and finance wordle! This guess the stock game features multiple categories with ticker guessing, stock trivia, and investing puzzles. Free daily stock challenge with live charts and hints.",
            "url": "https://wallstreetle.com/start",
            "inLanguage": "en-US",
            "author": {
              "@type": "Person",
              "name": "Wallstreetle Team",
              "url": "https://wallstreetle.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Wallstreetle",
              "logo": {
                "@type": "ImageObject",
                "url": "https://wallstreetle.com/logo512.png",
                "width": 512,
                "height": 512
              }
            },
            "datePublished": "2024-09-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "mainEntity": {
              "@type": "Game",
              "name": "Wallstreetle Stock Guessing Game",
              "description": "Stock wordle and finance wordle - a daily stock challenge where you guess the stock game using charts, ticker guessing, and financial metrics. Free equity game and investing puzzle.",
              "author": {
                "@type": "Person",
                "name": "Wallstreetle Team",
                "url": "https://wallstreetle.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Wallstreetle",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://wallstreetle.com/logo512.png",
                  "width": 512,
                  "height": 512
                }
              },
              "genre": ["Puzzle", "Educational", "Finance"],
              "gamePlatform": "Web Browser",
              "numberOfPlayers": 1,
              "inLanguage": "en-US"
            }
          })}
        </script>
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-background dark:bg-gray-900">
        <StartBackgroundTicker />

        {/* Desktop: Circular layout (lg+ breakpoint) */}
        <div className="relative hidden lg:block z-10">
          <StartCategoryCircle
            categories={CATEGORIES}
            hoveredCategory={hoveredCategory}
            onCategoryHover={setHoveredCategory}
            onCategoryClick={handleCategoryClick}
            isCategoryLocked={isCategoryLockedMemo}
          />
        </div>

        {/* Mobile: Grid layout (< lg breakpoint) */}
        <div className="relative block lg:hidden z-10">
          <StartCategoryGrid
            categories={CATEGORIES}
            selectedCategory={selectedCategoryMobile}
            onCategoryTap={handleMobileTap}
            onContinue={handleMobileContinue}
            isCategoryLocked={isCategoryLockedMemo}
          />
        </div>
      </div>
      {CategoryLockModal}
    </Layout>
  );
}
